import {
  Button,
  Flex,
  Modal,
  type ModalProps,
  Space,
  Table,
  Text,
} from '@/components';
import { MAXIMUM_SEARCH_HISTORY_LIMIT } from '@/features/project/_utils/constants';
import { getAmountSearchTagLabel } from '@/features/search/_utils/projects-search';
import { TABLE_BUTTON_WIDTH } from '@/utils';
import dayjs from 'dayjs';
import { type FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchHistory } from '../../_hooks/search-projects/local-storage';
import { useProjectsPageSearchQueryStates } from '../../_hooks/search-projects/search-query-states';
import { convertFormulaConditionsToAmountSearchConditions } from '../../_utils/projects/convert-request-params';

type Props = ModalProps & {
  onCancel: () => void;
  open: boolean;
  onSearch: () => void;
};

export const SearchHistoryModal: FC<Props> = ({ onCancel, open, onSearch }) => {
  const { t } = useTranslation();
  const { setSearchQueryStates } = useProjectsPageSearchQueryStates();
  const { searchHistoryData, setSearchHistoryStorage } = useSearchHistory();

  return (
    <Modal
      open={open}
      width={1200}
      onCancel={onCancel}
      title={t('search-history')}
      footer={<Button onClick={onCancel}>{t('close')}</Button>}
    >
      <Flex vertical gap={16}>
        <Text
          text={t('search-history-limit-information', {
            limit: MAXIMUM_SEARCH_HISTORY_LIMIT,
          })}
        />
        <Table
          enableSticky={false}
          dataSource={searchHistoryData?.sort((a, b) =>
            dayjs(b.datetime).isAfter(a.datetime) ? 1 : -1,
          )}
          scroll={{ y: 400 }}
          columns={[
            {
              title: t('search-history-datetime'),
              dataIndex: 'datetime',
              width: 100,
              render: (value) => dayjs(value).format('YYYY/MM/DD HH:mm'),
            },
            {
              title: t('search-condition'),
              dataIndex: 'searchHistoryLabelsMap',
              width: 400,
              render: (_, record) => (
                <SearchHistoryLabels
                  searchHistoryLabelsMap={record.searchHistoryLabelsMap}
                />
              ),
            },
            {
              title: '',
              key: 'actions',
              width: TABLE_BUTTON_WIDTH,
              render: (_, record) => (
                <Button
                  type="link"
                  onClick={async () => {
                    onSearch();

                    setSearchQueryStates((prev) => {
                      // 既存のパラメータをすべてnullに設定
                      const clearedState = Object.fromEntries(
                        Object.keys(prev).map((key) => [key, null]),
                      );
                      // 新しい検索条件を適用
                      return {
                        ...clearedState,
                        ...record.filterConditions,
                      };
                    });
                    setSearchHistoryStorage(
                      record.filterConditions,
                      record.searchHistoryLabelsMap,
                    );
                    onCancel();
                  }}
                >
                  {t('search')}
                </Button>
              ),
            },
          ]}
        />
      </Flex>
    </Modal>
  );
};

const SearchHistoryLabels = ({
  searchHistoryLabelsMap,
}: {
  searchHistoryLabelsMap:
    | {
        label: string;
        value: string | number;
      }[]
    | undefined;
}) => {
  const { t } = useTranslation();
  return searchHistoryLabelsMap?.flatMap(({ label, value }, index, array) => {
    if (!value) {
      return [];
    }

    // formula_conditions の場合は特別な表示処理
    if (
      label.includes('formula_conditions') ||
      (typeof value === 'string' && value.startsWith('['))
    ) {
      const conditions = convertFormulaConditionsToAmountSearchConditions(
        value.toString(),
      );
      if (conditions && conditions.length > 0) {
        // 各条件をカンマ区切りで表示
        const conditionLabels = conditions
          .map((condition) => {
            const label = getAmountSearchTagLabel({
              ...condition,
              amountSearchBaseYearWithBudgetTypeEnd:
                condition.amountSearchBaseYearWithBudgetTypeEnd ?? null,
            });
            return label || '';
          })
          .filter(Boolean);

        if (conditionLabels.length > 0) {
          return (
            <Fragment key={`${label}_${value}`}>
              <Space
                size={4}
                wrap
                split=":"
                style={{
                  marginRight: index < array.length - 1 ? 16 : undefined,
                }}
              >
                <Text text={t('formula-conditions')} color="tertiary" />
                <Flex align="center" gap={4}>
                  <Text text={conditionLabels.join(', ')} />
                </Flex>
              </Space>
            </Fragment>
          );
        }
      }
    }

    return (
      <Fragment key={`${label}_${value}`}>
        <Space
          size={4}
          wrap
          split=":"
          style={{
            marginRight: index < array.length - 1 ? 16 : undefined,
          }}
        >
          <Text text={label} color="tertiary" />
          <Flex align="center" gap={4}>
            <Text text={value.toString()} />
          </Flex>
        </Space>
      </Fragment>
    );
  });
};
